import { RootState } from "../../index";
import { createSelector } from "@reduxjs/toolkit";
import { DataStatus } from "../../types";
import { getCurrentSemanticMapId } from "Slices/appState";

const getMissionPlans = (state: RootState) => state.missionPlans.data;
export const getLoadedMissionPlansSemanticMapIds = (state: RootState) =>
  state.missionPlans.loadedSemanticMapIds;
export const getMissionPlanModalItemsDictSemanticMapId = createSelector(
  getMissionPlans,
  (missionPlans) => {
    return missionPlans.reduce<Record<string, Array<ModalItem>>>(
      (acc, missionPlan) => {
        const currentMissionPlanSemanticMapId = missionPlan["semanticMap"];

        if (acc[currentMissionPlanSemanticMapId]) {
          acc[currentMissionPlanSemanticMapId] = [
            ...acc[currentMissionPlanSemanticMapId],
            {
              id: missionPlan.id,
              text: missionPlan.name,
            },
          ];
        } else {
          acc[currentMissionPlanSemanticMapId] = [
            {
              id: missionPlan.id,
              text: missionPlan.name,
            },
          ];
        }
        return acc;
      },
      {}
    );
  }
);
const getMissionPlansStatus = (state: RootState) => state.missionPlans.status;
export const getIsMissionPlansLoaded = createSelector(
  getMissionPlansStatus,
  (status) => status === DataStatus.succeeded
);
export const getIsMissionPlansLoading = createSelector(
  getMissionPlansStatus,
  (status) => status === DataStatus.loading
);
export const getIsMissionPlansLoadedForCurrentSemanticMap = createSelector(
  getIsMissionPlansLoaded,
  getCurrentSemanticMapId,
  getLoadedMissionPlansSemanticMapIds,
  (isMissionPlansLoaded, currentSemanticMapId, loadedSemanticMapIds) =>
    isMissionPlansLoaded &&
    loadedSemanticMapIds.includes(currentSemanticMapId as string)
);
export const getMissionsPlansByCurrentSemanticMap = createSelector(
  getMissionPlans,
  getCurrentSemanticMapId,
  (missionPlans, currentSemanticMapId) =>
    missionPlans
      .filter((missionPlan) => missionPlan.semanticMap === currentSemanticMapId)
      .map((missionPlan) => ({
        ...missionPlan,
        stops: missionPlan.stops?.filter((stop) => !stop.deletedAt) || [],
      }))
      .sort((a, b) => {
        // Sort by updatedAt in descending order (most recent first)
        const dateA = new Date(a.updatedAt).getTime();
        const dateB = new Date(b.updatedAt).getTime();
        return dateB - dateA;
      })
);
export const getIsMissionPlansLoadingError = createSelector(
  getMissionPlansStatus,
  (status) => status === DataStatus.failed
);

// Get cached versions for a mission by masterMissionId
export const getMissionVersions = (masterMissionId: string) =>
  createSelector(
    (state: RootState) => state.missionPlans.versions,
    (versions) => versions[masterMissionId] || null
  );

// Get mission plan by ID (including all versions stored as separate missions)
export const getMissionPlanById = (missionId: string) =>
  createSelector(
    getMissionPlans,
    (missionPlans) =>
      missionPlans.find((mission) => mission.id === missionId) || null
  );
